import * as React from 'react';
import { Gray100, Segmented, SegmentedItem, TextArea, TextInput, typeCompact12RegularProperties } from '@traceair/tds';
import { stylesheet, style } from 'typestyle';
import { CopyLinkIcon } from '@traceair/webapp-icons';

type Props = {
  method: 'link' | 'bank';
  paymentLink: string;
  bankDetails: string;
  onMethodChange: (m: 'link' | 'bank') => void;
  onLinkChange: (v: string) => void;
  onBankChange: (v: string) => void;
  isLinkError?: boolean;
  isBankError?: boolean;
  linkErrorMessage?: string;
  bankErrorMessage?: string;
};

const css = stylesheet({
  root: { display: 'flex', flexDirection: 'column', gap: 4 },
  panel: { display: 'flex', flexDirection: 'column', gap: 16 },
  label: { ...typeCompact12RegularProperties, margin: 0, color: Gray100 },
  segmentedContainer: { width: 'fit-content' },
});

const PaymentMethodPanel: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className={css.panel}>
    <p className={css.label}>{label}</p>
    {children}
  </div>
);

export const PaymentSection: React.FC<Props> = ({
  method,
  paymentLink,
  bankDetails,
  onMethodChange,
  onLinkChange,
  onBankChange,
  isLinkError,
  isBankError,
  linkErrorMessage,
  bankErrorMessage,
}) => (
  <div className={css.root}>
    <div className={css.segmentedContainer}>
      <Segmented value={method} onChange={(val) => onMethodChange(val as 'link' | 'bank')}>
        <SegmentedItem text="Payment link" value={'link'} />
        <SegmentedItem text="Bank account details" value={'bank'} />
      </Segmented>
    </div>
    {method === 'link' ? (
      <PaymentMethodPanel label="Third-party payment service">
        <TextInput
          value={paymentLink}
          leftCell={<CopyLinkIcon />}
          placeholder="https://..."
          isError={isLinkError}
          onChange={onLinkChange}
          errorMessage={linkErrorMessage}
        />
      </PaymentMethodPanel>
    ) : (
      <PaymentMethodPanel label="Your full bank details">
        <TextArea
          value={bankDetails}
          placeholder="Account holder name, routing number, account number, e.g. Jane Smith, 123456789, 000123456789"
          className={style({ minHeight: 143 })}
          isError={isBankError}
          errorMessage={bankErrorMessage}
          onChange={onBankChange}
        />
      </PaymentMethodPanel>
    )}
  </div>
);
